import { BaseShader } from './base-shader';
import { ShaderBehavior } from './shader-types';

/**
 * Abstract base class for texture-like shaders that should crop/expand rather than scale.
 * These shaders represent continuous patterns, effects, or simulations where maintaining
 * the intrinsic "texture scale" is more important than showing the complete image.
 *
 * Examples: fire, water waves, noise patterns, particle effects
 */
export abstract class TextureShader extends BaseShader {
  /**
   * Generate shader code that uses reference resolution for consistent texture scaling.
   * Texture shaders should use u_reference_resolution for UV calculations and handle
   * out-of-bounds areas appropriately (usually with background color).
   */
  abstract generate(): string;

  /**
   * Get the shader code preamble that handles texture-style UV calculation
   */
  protected getTextureUVPreamble(): string {
    return `
      // Canvas size controls amount of content, reference resolution controls zoom
      // Use reference resolution as the base scale for UV coordinates
      vec2 baseUV = gl_FragCoord.xy / u_reference_resolution;

      // Center the pattern when canvas is larger than reference
      vec2 offset = (u_resolution - u_reference_resolution) * 0.5;
      vec2 centeredCoord = gl_FragCoord.xy - offset;
      baseUV = centeredCoord / u_reference_resolution;

      // Apply rotation around center (0.5, 0.5)
      vec2 uv = baseUV;
      if (u_rotation != 0.0) {
        float angle = radians(u_rotation);
        vec2 center = vec2(0.5, 0.5);
        vec2 rotatedUV = uv - center;
        float cosA = cos(angle);
        float sinA = sin(angle);
        rotatedUV = vec2(
          rotatedUV.x * cosA - rotatedUV.y * sinA,
          rotatedUV.x * sinA + rotatedUV.y * cosA
        );
        uv = rotatedUV + center;
      }

      // No out-of-bounds check - pattern extends naturally
      bool isOutOfBounds = false;
    `;
  }

  /**
   * Get the standard uniforms that texture shaders need
   */
  protected getTextureUniforms(): string {
    return `
      uniform vec2 u_resolution;
      uniform vec2 u_reference_resolution;
      uniform float u_time;
      uniform float u_rotation;
    `;
  }
}
